import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api, unwrap } from '@/api/client';
import type { components } from '@/api/schema';

type S = components['schemas'];
export type Tipo = 'class' | 'workshop';
export type Letra = 'A' | 'B' | 'C' | 'D';
export type ClassList = S['ClassList'];
export type ClassDetail = S['ClassDetail'];
export type TabKey = ClassDetail['tabs'][number]['key'];
export type ClassCase = S['ClassCase'];
export type ClassQuiz = S['ClassQuiz'];

/** Respuestas que el OpenAPI aún no tipa con detalle (forma de `classes.service.ts`). */
export interface Video {
  id: number;
  name: string;
  provider: 'vimeo' | 'youtube';
  videoId: string;
  embedUrl: string;
}
export interface Lamina {
  id: number;
  name: string;
  imageUrl: string;
}
export interface Guia {
  id: number;
  name: string;
  kind: 'pdf' | 'image';
  fileUrl: string;
  thumbUrl: string;
}
export interface ResumenDeClase {
  libraryId: number;
  name: string;
  coverUrl: string | null;
}
export interface PresentacionDeClase {
  presentationId: number;
  savedSlide: number;
  slides: { number: number; imageUrl: string }[];
}
export interface RespuestaCaso {
  case: string;
  question: string;
  yourAnswer: string;
  correctAnswer: string;
  correct: boolean;
  justification: string;
}

/** Las URLs firmadas viven 60 s: los visores las renuevan antes de que venzan. */
export const URLS_FIRMADAS = { staleTime: 40_000, refetchInterval: 45_000 } as const;

const base = (tipo: Tipo) => (tipo === 'class' ? '/api/v1/classes' : '/api/v1/workshops');

/** GET genérico de una subruta de clase (`openapi-fetch` no admite rutas dinámicas sin tipar). */
async function getClase<T>(tipo: Tipo, id: number, sub = ''): Promise<T> {
  const res = await api.GET(`${base(tipo)}/{id}${sub}` as '/api/v1/classes/{id}', {
    params: { path: { id: String(id) } },
  });
  return unwrap<T>(res as never);
}

export const useClases = (tipo: Tipo) =>
  useQuery({
    queryKey: ['clases', tipo],
    queryFn: () =>
      unwrap<ClassList>(
        tipo === 'class' ? api.GET('/api/v1/classes') : api.GET('/api/v1/workshops'),
      ),
  });

export const useClase = (tipo: Tipo, id: number) =>
  useQuery({
    queryKey: ['clase', tipo, id],
    queryFn: () => getClase<ClassDetail>(tipo, id),
    retry: false,
  });

export function usePestana<T>(
  tipo: Tipo,
  id: number,
  sub: string,
  { enabled = true, firmadas = false }: { enabled?: boolean; firmadas?: boolean } = {},
) {
  return useQuery({
    queryKey: ['clase', tipo, id, sub],
    queryFn: () => getClase<T>(tipo, id, sub),
    enabled,
    retry: false,
    ...(firmadas ? URLS_FIRMADAS : {}),
  });
}

async function enviar<T>(method: 'POST' | 'PUT', path: string, body?: unknown): Promise<T> {
  const res = await (method === 'POST' ? api.POST : api.PUT)(
    path as never,
    {
      body: body as never,
    } as never,
  );
  return unwrap<T>(res as never);
}

export function useAccionClase(tipo: Tipo, id: number) {
  const qc = useQueryClient();
  const invalidar = () => qc.invalidateQueries({ queryKey: ['clase', tipo, id] });
  return {
    responderCaso: useMutation({
      mutationFn: (option: Letra) =>
        enviar<{ saved: boolean; message: string }>(
          'POST',
          `${base(tipo)}/${String(id)}/case-answers`,
          { option },
        ),
      onSuccess: invalidar,
    }),
    responderQuiz: useMutation({
      mutationFn: (option: Letra) =>
        enviar<ClassQuiz['result']>('POST', `${base(tipo)}/${String(id)}/quiz-answers`, {
          option,
        }),
      onSuccess: () => qc.invalidateQueries({ queryKey: ['clase', tipo, id, '/quiz'] }),
    }),
    guardarDiapositiva: useMutation({
      mutationFn: (slide: number) =>
        enviar('PUT', `${base(tipo)}/${String(id)}/presentation-progress`, { slide }),
    }),
    sumarVista: useMutation({
      mutationFn: () => enviar<{ views: number }>('POST', `${base(tipo)}/${String(id)}/views`),
      onSuccess: () => qc.invalidateQueries({ queryKey: ['clases', tipo] }),
    }),
    resena: useMutation({
      mutationFn: (r: { stars: number; comment: string }) =>
        enviar('PUT', `${base(tipo)}/${String(id)}/review`, r),
      onSuccess: invalidar,
    }),
  };
}

// --------------------------------------------------------------------- biblioteca

export type PresentationList = S['PresentationList'];
export type Presentation = S['Presentation'];
export type FlashcardList = S['FlashcardList'];
export type FlashcardViewer = S['FlashcardViewer'];
export type VideotipList = S['VideotipList'];
export type Videotip = S['Videotip'];
export type GpcGuideList = S['GpcGuideList'];
export type GpcSummaryList = S['GpcSummaryList'];
export type GpcSummary = S['GpcSummary'];
export type ContentCases = S['ContentCases'];
export type ContentCase = S['ContentCase'];
export type ContentNotes = S['ContentNotes'];
export type TipoContenido = 'presentations' | 'flashcards' | 'videotips' | 'manuals';

export const usePresentaciones = (membership?: number) =>
  useQuery({
    queryKey: ['biblioteca', 'presentaciones', membership ?? 0],
    queryFn: () =>
      unwrap<PresentationList>(
        api.GET('/api/v1/library/presentations', {
          params: { query: membership ? { membership: String(membership) } : {} },
        }),
      ),
    placeholderData: keepPreviousData,
    ...URLS_FIRMADAS,
  });

export const usePresentacion = (id: number) =>
  useQuery({
    queryKey: ['biblioteca', 'presentacion', id],
    queryFn: () =>
      unwrap<Presentation>(
        api.GET('/api/v1/library/presentations/{id}', { params: { path: { id: String(id) } } }),
      ),
    retry: false,
    ...URLS_FIRMADAS,
  });

export function useGuardarAvancePresentacion(id: number) {
  return useMutation({
    mutationFn: (slide: number) =>
      unwrap(
        api.PUT('/api/v1/library/presentations/{id}/progress', {
          params: { path: { id: String(id) } },
          body: { slide },
        }),
      ),
  });
}

export const useFlashcards = (evaluadas: boolean) =>
  useQuery({
    queryKey: ['biblioteca', 'flashcards', evaluadas],
    queryFn: () =>
      unwrap<FlashcardList>(
        api.GET('/api/v1/library/flashcards', {
          params: { query: { kind: evaluadas ? 'evaluated' : 'all' } },
        }),
      ),
    ...(evaluadas ? URLS_FIRMADAS : {}),
  });

export const useVisorFlashcards = (id: number) =>
  useQuery({
    queryKey: ['biblioteca', 'flashcard', id],
    queryFn: () =>
      unwrap<FlashcardViewer>(
        api.GET('/api/v1/library/flashcards/{id}', { params: { path: { id: String(id) } } }),
      ),
    retry: false,
    ...URLS_FIRMADAS,
  });

export const vistaFlashcard = (id: number) =>
  api
    .POST('/api/v1/library/flashcards/{id}/views', { params: { path: { id: String(id) } } })
    .catch(() => undefined);

export const useVideotips = () =>
  useQuery({
    queryKey: ['biblioteca', 'videotips'],
    queryFn: () => unwrap<VideotipList>(api.GET('/api/v1/library/videotips')),
  });

export const useVideotip = (id: number) =>
  useQuery({
    queryKey: ['biblioteca', 'videotip', id],
    queryFn: () =>
      unwrap<Videotip>(
        api.GET('/api/v1/library/videotips/{id}', { params: { path: { id: String(id) } } }),
      ),
    retry: false,
  });

export const useGuiasGpc = () =>
  useQuery({
    queryKey: ['biblioteca', 'guias-gpc'],
    queryFn: () => unwrap<GpcGuideList>(api.GET('/api/v1/library/gpc-guides')),
    ...URLS_FIRMADAS,
  });

export const useResumenesGpc = () =>
  useQuery({
    queryKey: ['biblioteca', 'resumenes-gpc'],
    queryFn: () => unwrap<GpcSummaryList>(api.GET('/api/v1/library/gpc-summaries')),
  });

export const useResumenGpc = (id: number) =>
  useQuery({
    queryKey: ['biblioteca', 'resumen-gpc', id],
    queryFn: () =>
      unwrap<GpcSummary>(
        api.GET('/api/v1/library/gpc-summaries/{id}', { params: { path: { id: String(id) } } }),
      ),
    retry: false,
    ...URLS_FIRMADAS,
  });

export const useNotas = (tipo: TipoContenido, id: number) =>
  useQuery({
    queryKey: ['biblioteca', 'notas', tipo, id],
    queryFn: () =>
      unwrap<ContentNotes>(
        api.GET('/api/v1/library/{type}/{id}/notes', {
          params: { path: { type: tipo, id: String(id) } },
        }),
      ),
  });

export const useCasosContenido = (tipo: TipoContenido, id: number, enabled = true) =>
  useQuery({
    queryKey: ['biblioteca', 'casos', tipo, id],
    queryFn: () =>
      unwrap<ContentCases>(
        api.GET('/api/v1/library/{type}/{id}/cases', {
          params: { path: { type: tipo, id: String(id) } },
        }),
      ),
    enabled,
  });

export function useResponderCasoContenido(tipo: TipoContenido, id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { caseId: number; option: Letra; seconds: number }) =>
      unwrap<ContentCase>(
        api.POST('/api/v1/library/{type}/{id}/cases/{caseId}/answers', {
          params: { path: { type: tipo, id: String(id), caseId: String(v.caseId) } },
          body: { option: v.option, seconds: v.seconds },
        }),
      ),
    onSuccess: (caso) => {
      qc.setQueryData<ContentCases>(['biblioteca', 'casos', tipo, id], (prev) =>
        prev
          ? {
              ...prev,
              answered: prev.cases.filter((c) => c.answered || c.caseId === caso.caseId).length,
              correct: prev.cases.filter((c) =>
                c.caseId === caso.caseId ? caso.isCorrect : c.isCorrect,
              ).length,
              cases: prev.cases.map((c) => (c.caseId === caso.caseId ? caso : c)),
            }
          : prev,
      );
    },
  });
}
